/* ============================================================
   Daytakt quick add, the plus on a product card.
   Adds through /cart/add.js with the Section Rendering API, so the
   theme's own cart drawer and header count come back rendered by
   Shopify rather than guessed at. After a successful add the drawer
   slides in and a dark bar confirms the line, then disappears.
   Loaded by every section that renders nx-product-card; the guard
   below keeps a second copy from binding twice.
   ============================================================ */
(function () {
  if (window.__nxQuickAdd) return;
  window.__nxQuickAdd = true;

  var openPanel = null;

  /* ---- the phone sheet is moved to the end of <body> -----------------
     A card's `.nxs-qa` carries `position: relative; z-index: 2` so the plus
     button sits over the photograph, which makes EVERY card its own stacking
     context. A sheet raised inside one of them can never reliably cover the
     others: its z-index only counts within its own context, the neighbours
     are worth the same 2, and ties go to document order — so every card
     after the open one drew its plus button through the dim. Raising the open
     card's z-index fixed that in Chromium and did not survive contact with
     the owner's phone.

     So on a phone the sheet stops living in the card. It is appended to
     <body>, where nothing is above it and nothing can be, and put back on
     close. `panelOwner` keeps the link the DOM no longer provides, because
     `button.closest('.nxs-qa')` is how the add handler finds its card.

     Desktop is untouched: there the panel is a small absolute box inside the
     picture, which is exactly where it should be, and moving it would break
     that. -------------------------------------------------------------- */
  var isPhone = function () { return window.matchMedia('(max-width: 749px)').matches; };
  var panelOwner = new WeakMap();

  function unportal(panel) {
    var home = panel && panel.__nxHome;
    if (!home) return;
    home.appendChild(panel);
    panel.classList.remove('nxs-qa__panel--sheet');
    panel.__nxHome = null;
  }

  /* `immediate` skips the closing animation. Used when one card is opened
     while another is still open: both classes change in the same tick, so the
     dim never blinks off and on between them. */
  function close(wrap, immediate) {
    if (!wrap) return;
    var panel = wrap.querySelector('.nxs-qa__panel') || wrap.__nxPanel;
    var toggle = wrap.querySelector('.nxs-qa__btn');
    var isSheet = panel && panel.classList.contains('nxs-qa__panel--sheet');

    if (panel) {
      clearTimeout(panel.__nxTimer);
      if (isSheet && !immediate) {
        /* Let it slide out before it is hidden and moved home — `hidden` and
           `appendChild` both stop a transition dead. */
        panel.classList.remove('is-in');
        panel.__nxTimer = setTimeout(function () {
          unportal(panel);
          panel.hidden = true;
        }, 340);
      } else {
        panel.classList.remove('is-in');
        unportal(panel);
        panel.hidden = true;
      }
    }
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    wrap.classList.remove('is-open');
    document.documentElement.classList.remove('nxs-qa-open');
    if (openPanel === wrap) openPanel = null;
  }

  function open(wrap) {
    if (openPanel && openPanel !== wrap) close(openPanel, true);
    var panel = wrap.querySelector('.nxs-qa__panel') || wrap.__nxPanel;
    var toggle = wrap.querySelector('.nxs-qa__btn');
    if (panel) {
      clearTimeout(panel.__nxTimer);
      panel.hidden = false;
    }
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    wrap.classList.add('is-open');
    openPanel = wrap;
    if (panel) {
      wrap.__nxPanel = panel;
      panelOwner.set(panel, wrap);
      if (isPhone()) {
        if (!panel.__nxHome) panel.__nxHome = panel.parentNode;
        panel.classList.add('nxs-qa__panel--sheet');
        document.body.appendChild(panel);
        document.documentElement.classList.add('nxs-qa-open');
        /* Two frames, not one. The element has just been moved and given the
           class that puts it off-screen; the browser has to record THAT as its
           starting point before the transition to `is-in` has anything to
           animate from. A single rAF lands in the same frame as the move on
           some builds and the sheet appears instantly. */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { panel.classList.add('is-in'); });
        });
      }
    }
    /* Every row starts answered. A panel that opens with nothing chosen makes
       the reader do the shop's work before it will quote a price, and with two
       rows it is twice as much of it. */
    if (panel) {
      panel.querySelectorAll('.nxs-qa__opts').forEach(function (row) {
        if (row.querySelector('.nxs-qa__opt.is-selected')) return;
        var f = row.querySelector('.nxs-qa__opt:not([disabled])');
        if (f) f.classList.add('is-selected');
      });
      resolve(panel);
    }
  }

  /* The panel asks one question per option and no longer knows which VARIANT a
     button means — it knows which value. The combination is matched against the
     matrix the card emits, positionally, in option order. Never by title: a
     title is a display string, and matching variants by their names is what put
     the $49 photograph on the $39 variant once already. */
  function variantMap(panel) {
    var el = panel.querySelector('[data-nx-qa-map]');
    if (!el) return [];
    if (!el.nxParsed) {
      try { el.nxParsed = JSON.parse(el.textContent); } catch (err) { el.nxParsed = []; }
    }
    return el.nxParsed;
  }

  function resolve(panel) {
    var add = panel.querySelector('.nxs-qa__add');
    if (!add || add.classList.contains('nxs-qa__add--solo')) return;
    var price = panel.querySelector('.nxs-qa__add-price');
    var msg = panel.querySelector('.nxs-qa__msg');

    var chosen = [];
    var missing = false;
    panel.querySelectorAll('.nxs-qa__opts').forEach(function (row) {
      var s = row.querySelector('.nxs-qa__opt.is-selected');
      if (!s) { missing = true; return; }
      chosen.push(s.dataset.nxQaVal);
    });

    var hit = null;
    if (!missing) {
      hit = variantMap(panel).filter(function (v) {
        return v.o && v.o.length === chosen.length && v.o.every(function (x, i) { return x === chosen[i]; });
      })[0] || null;
    }

    /* A combination the catalogue does not sell disables the button and says so,
       rather than falling back to some other variant. */
    if (!hit || !hit.a) {
      add.disabled = true;
      add.dataset.variant = '';
      if (msg) {
        if (hit && !hit.a) { msg.textContent = 'That combination is sold out.'; msg.hidden = false; }
        else { msg.hidden = true; }
      }
      return;
    }

    add.disabled = false;
    add.dataset.variant = hit.id;
    add.dataset.label = hit.t || '';
    if (price) price.textContent = hit.p;
    if (msg) msg.hidden = true;
  }

  function select(opt) {
    var panel = opt.closest('.nxs-qa__panel');
    var row = opt.closest('.nxs-qa__opts');
    /* Only this row's siblings clear. Clearing the whole panel is what made a
       second row impossible: choosing a colour unchose the model. */
    (row || panel).querySelectorAll('.nxs-qa__opt').forEach(function (o) { o.classList.remove('is-selected'); });
    opt.classList.add('is-selected');
    resolve(panel);
  }

  /* ---- toast ------------------------------------------------------ */
  var toastEl = null, toastTimer = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nxs-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(function () { toastEl.classList.add('is-in'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-in'); }, 2600);
  }

  /* ---- paint the theme's own cart from the sections payload -------- */
  function paintCart(sections) {
    if (!sections) return;
    var parser = new DOMParser();

    if (sections['cart-drawer']) {
      var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      var freshInner = doc.querySelector('#CartDrawer');
      var liveInner = document.querySelector('#CartDrawer');
      if (freshInner && liveInner) {
        if (window.nxWallets) window.nxWallets(freshInner); /* the Meta drawer's set of wallets, before they draw */
        liveInner.innerHTML = freshInner.innerHTML;
      }

      var freshEl = doc.querySelector('cart-drawer');
      var liveEl = document.querySelector('cart-drawer');
      if (freshEl && liveEl) liveEl.classList.toggle('is-empty', freshEl.classList.contains('is-empty'));

      var freshItems = doc.querySelector('cart-drawer-items');
      var liveItems = document.querySelector('cart-drawer-items');
      if (freshItems && liveItems) liveItems.classList.toggle('is-empty', freshItems.classList.contains('is-empty'));
    }

    if (sections['cart-icon-bubble']) {
      var bdoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      /* ⚠ The Section Rendering API wraps its answer in
         `<div id="shopify-section-cart-icon-bubble">`. The live page has no
         such element — there the id belongs to the <a> that IS the cart link.
         Looking for `#cart-icon-bubble` in the response therefore found
         nothing and the count silently never updated: an item went into the
         cart, the drawer painted, and the header still read empty. Take the
         section wrapper first and the bare id only as a fallback. */
      var freshBubble =
        bdoc.querySelector('#shopify-section-cart-icon-bubble') ||
        bdoc.querySelector('#cart-icon-bubble');
      var liveBubble = document.querySelector('#cart-icon-bubble');
      if (freshBubble && liveBubble) liveBubble.innerHTML = freshBubble.innerHTML;
    }
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); return true; } catch (e) { /* fall through */ }
    }
    var icon = document.querySelector('#cart-icon-bubble');
    if (icon) { icon.click(); return true; }
    return false;
  }

  /* ---- add -------------------------------------------------------- */
  async function add(button) {
    var panel = button.closest('.nxs-qa__panel');
    /* The sheet may have been moved to <body>, so the DOM no longer leads
       back to the card. panelOwner is that link. */
    var wrap = button.closest('.nxs-qa') || panelOwner.get(panel);
    var msg = panel.querySelector('.nxs-qa__msg');
    var id = button.dataset.variant;
    if (!id) return;

    var card = button.closest('.nxs-card') || (wrap && wrap.closest('.nxs-card'));
    var titleEl = card && card.querySelector('.nxs-card__h a');
    var name = titleEl ? titleEl.textContent.trim() : 'Item';
    var variantLabel = button.dataset.label || '';

    button.disabled = true;
    var label = button.innerHTML;
    button.innerHTML = 'Adding';

    try {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          attributes: window.nxCartAttrs,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      if (!res.ok) throw new Error('add failed');
      var data = await res.json();

      paintCart(data.sections);
      button.innerHTML = label;
      button.disabled = false;
      close(wrap);
      toast(name + (variantLabel ? ', ' + variantLabel : '') + ' · 1 added to your cart');
      openDrawer();
    } catch (e) {
      button.disabled = false;
      button.innerHTML = label;
      if (msg) { msg.hidden = false; msg.textContent = 'That did not go through. Please try the product page.'; }
    }
  }

  /* ---- the shared way into the cart -------------------------------
     Everything that adds to the cart goes through here, so there is one
     answer to "what happens after an add" instead of three. The plus on a
     card had the drawer; the Product Story's own ADD TO CART posted its
     form and landed the reader on /cart, and the bundle band redirected
     there by hand. A full page for something the drawer already shows is
     a worse answer, and it was the only place in the store that did it. */
  async function addItems(items, name) {
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items,
        /* nx_entry=meta for a visitor from a Meta ad (layout/theme.liquid), so the drawer this add renders already
           has the wallets under Checkout; undefined for everyone else, and JSON.stringify leaves it out */
        attributes: window.nxCartAttrs,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    });
    if (!res.ok) throw new Error('add failed');
    var data = await res.json();
    /* On /cart the page itself is the cart (owner, 2026-10-12: the gift card there too). The drawer painted over it
       left the page's lines and subtotal one add behind, so the page is drawn again instead. */
    if (window.location.pathname === '/cart') { window.location.reload(); return data; }
    paintCart(data.sections);
    if (name) toast(name);
    openDrawer();
    return data;
  }

  window.nxCart = { add: addItems, paint: paintCart, open: openDrawer, toast: toast };

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('.nxs-qa__btn');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('.nxs-qa');
      if (wrap.classList.contains('is-open')) close(wrap); else open(wrap);
      return;
    }
    var opt = e.target.closest('.nxs-qa__opt');
    if (opt && !opt.disabled) { e.preventDefault(); select(opt); return; }

    var addBtn = e.target.closest('.nxs-qa__add');
    if (addBtn) { e.preventDefault(); add(addBtn); return; }

    var closeBtn = e.target.closest('.nxs-qa__close');
    if (closeBtn) {
      e.preventDefault();
      close(closeBtn.closest('.nxs-qa') || panelOwner.get(closeBtn.closest('.nxs-qa__panel')));
      return;
    }

    /* on a phone the panel rises as a sheet over a dimmed page. The dim is a
       pseudo-element of <html> — a tap on it has <html> as its target, and a
       tap anywhere outside the sheet closes it either way. */
    if (openPanel && e.target === openPanel) { close(openPanel); return; }

    if (openPanel && !e.target.closest('.nxs-qa') && !e.target.closest('.nxs-qa__panel')) close(openPanel);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openPanel) close(openPanel);
  });
})();
